/**
 * GTM dataLayer events — ported from the headless storefront (lib/gtm.ts).
 *
 * GTM itself + window.dataLayer are loaded by the shop.metafields.foxtheme.code_head
 * metafield (see layout/theme.liquid). This module only PUSHES events.
 *
 * Two payload schemas per event, matching the headless template:
 *  - standard GA4 (`GtmProduct`)  -> numeric price/value/quantity
 *  - eyk twin (`_eyk`, `EykItem`) -> string price/value/quantity
 *
 * Data comes from two sources:
 *  - server-rendered JSON <script> blocks (product / 404) and card data-gtm-item attributes
 *    (collection list, read from the grid container) for on-page events
 *  - the Shopify AJAX cart JSON + FoxTheme.pubsub for cart/filter/interaction events
 */
(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  var DEFAULT_OPTION = 'Default Title';
  var page = window.__gtmPage || {};
  var currency = page.currency || '';

  function push(obj) {
    try {
      window.dataLayer.push(obj);
    } catch (e) {
      /* swallow — tracking must never break the page */
    }
  }

  function toNumber(v) {
    var n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  }

  function isDefaultVariant(title) {
    return !title || title === DEFAULT_OPTION;
  }

  // ----------------------------------------------------------------- builders
  // From a normalized product-data object (see snippets/gtm-product-data.liquid)
  function standardFromData(d, ctx) {
    ctx = ctx || {};
    return {
      item_id: d.sku || String(d.variant_id),
      item_name: d.name,
      discount: d.compare_at > 0 ? d.compare_at : 0,
      index: ctx.index,
      item_brand: d.vendor || '',
      item_category: d.category || '',
      item_category2: d.category2 || '',
      item_category3: d.category3 || '',
      item_variant: isDefaultVariant(d.variant_title) ? '' : d.variant_title,
      price: toNumber(d.price),
      item_list_id: ctx.listId,
      item_list_name: ctx.listName,
      quantity: ctx.quantity
    };
  }

  function eykFromData(d, ctx) {
    ctx = ctx || {};
    return {
      image_url: d.image || undefined,
      item_brand: d.vendor || '',
      item_category: d.category || '',
      item_id: String(d.product_id),
      item_name: d.name,
      item_sku: d.sku || '',
      item_variant: String(d.variant_id),
      price: String(toNumber(d.price)),
      quantity: ctx.quantity != null ? String(ctx.quantity) : '1',
      variant_name: d.variant_title || DEFAULT_OPTION,
      index: ctx.index
    };
  }

  // Cart line items (cart.js / cart-add) carry no collection data. Headless uses
  // the product's FIRST collection handle (full, not split) for cart events
  // (`cart_category`). Cache it from the on-page product data (cards + product
  // page + list), keyed by variant/product id, and resolve cart lines against it.
  var categoryCache = {};

  function cacheCategory(d) {
    if (!d) return;
    var c = d.cart_category || '';
    if (d.variant_id != null) categoryCache['v' + d.variant_id] = c;
    if (d.product_id != null) categoryCache['p' + d.product_id] = c;
  }

  function lineCategory(line) {
    var c = categoryCache['v' + line.variant_id];
    if (c == null) c = categoryCache['p' + line.product_id];
    return c != null ? c : '';
  }

  function parseCard(el) {
    try {
      return JSON.parse(el.getAttribute('data-gtm-item'));
    } catch (e) {
      return null;
    }
  }

  function buildCategoryCache() {
    var cards = document.querySelectorAll('[data-gtm-item]');
    for (var i = 0; i < cards.length; i++) {
      cacheCategory(parseCard(cards[i]));
    }
    cacheCategory(readJSON(document.querySelector('[data-gtm-view-item]')));
  }

  // Collection list: the grid container carries list_id / list_name / currency,
  // its cards carry the per-item data (same fields as gtm-product-data), in DOM order.
  function readListData() {
    var grid = document.querySelector('[data-gtm-list-id]');
    if (!grid) return null;
    var items = [];
    var cards = grid.querySelectorAll('[data-gtm-item]');
    for (var i = 0; i < cards.length; i++) {
      var d = parseCard(cards[i]);
      if (d) items.push(d);
    }
    return {
      list_id: grid.getAttribute('data-gtm-list-id'),
      list_name: grid.getAttribute('data-gtm-list-name'),
      currency: grid.getAttribute('data-gtm-currency'),
      items: items
    };
  }

  // From a Shopify cart / cart-add line item (cents-based money)
  function standardFromLine(line, index, quantityOverride) {
    var qty = quantityOverride != null ? quantityOverride : line.quantity;
    var discount = 0;
    if (line.original_line_price != null && line.final_line_price != null && line.quantity) {
      discount = (line.original_line_price - line.final_line_price) / line.quantity / 100;
    }
    return {
      item_id: line.sku || '',
      item_name: line.product_title || line.title,
      discount: discount > 0 ? discount : 0,
      index: index,
      item_brand: line.vendor || '',
      item_category: lineCategory(line),
      item_variant: isDefaultVariant(line.variant_title) ? '' : line.variant_title,
      price: (line.final_price != null ? line.final_price : line.price) / 100,
      quantity: qty
    };
  }

  function eykFromLine(line, index, quantityOverride) {
    var qty = quantityOverride != null ? quantityOverride : line.quantity;
    var img = (line.featured_image && line.featured_image.url) || line.image || undefined;
    return {
      image_url: img,
      item_brand: line.vendor || '',
      item_category: lineCategory(line),
      item_id: String(line.variant_id),
      item_name: line.product_title || line.title,
      item_sku: line.sku || '',
      item_variant: String(line.variant_id),
      price: String((line.final_price != null ? line.final_price : line.price) / 100),
      quantity: String(qty),
      variant_name: line.variant_title || DEFAULT_OPTION,
      index: index
    };
  }

  function readJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // ------------------------------------------------------------- page load
  function firePageView() {
    var data = {
      page: { title: page.title, type: page.type },
      user: { referrer: document.referrer || null, user_type: undefined, language: page.language || null }
    };
    push(Object.assign({ event: 'page_view' }, data));
    push(Object.assign({ event: 'page_view_eyk' }, data));
  }

  function fireViewItem() {
    var d = readJSON(document.querySelector('[data-gtm-view-item]'));
    if (!d) return;
    push({
      event: 'view_item',
      _clear: true,
      ecommerce: { currency: d.currency || currency, value: toNumber(d.price), items: [standardFromData(d, {})] }
    });
    push({
      event: 'view_item_eyk',
      _clear: true,
      ecommerce: { currency: d.currency || currency, value: String(toNumber(d.price)), items: [eykFromData(d, { quantity: 1 })] }
    });
  }

  function fireViewItemList() {
    var data = readListData();
    if (!data || !data.items.length) return;
    push({
      event: 'view_item_list',
      _clear: true,
      ecommerce: {
        item_list_id: data.list_id,
        item_list_name: data.list_name,
        items: data.items.map(function (d) {
          return standardFromData(d, { index: d.index, listId: data.list_id, listName: data.list_name });
        })
      }
    });
    push({
      event: 'view_collection_eyk',
      _clear: true,
      ecommerce: {
        collection_id: data.list_id,
        item_list_name: data.list_name || 'Products',
        currency: data.currency || currency,
        items: data.items.map(function (d) {
          return eykFromData(d, { index: d.index });
        })
      }
    });
  }

  function fireView404() {
    if (!document.querySelector('[data-gtm-view-404]')) return;
    push({ event: 'view_404', user: { referrer: document.referrer || null, language: page.language || null } });
  }

  // ------------------------------------------------------------- cart state
  var prevLines = {}; // key -> { qty, line }
  var lastCart = null;

  function indexLines(cart) {
    var map = {};
    if (cart && cart.items) {
      cart.items.forEach(function (l, i) {
        map[l.key] = { qty: l.quantity, line: l, index: i };
      });
    }
    return map;
  }

  function seedCart() {
    var url = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_url) || '/cart';
    fetch(url + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        lastCart = cart;
        prevLines = indexLines(cart);
      })
      .catch(function () {});
  }

  function detectRemovals(cart) {
    if (!cart || !cart.items) return;
    var next = indexLines(cart);
    Object.keys(prevLines).forEach(function (key) {
      var prev = prevLines[key];
      var cur = next[key];
      var newQty = cur ? cur.qty : 0;
      if (newQty < prev.qty) {
        var removedQty = prev.qty - newQty;
        var line = cur ? cur.line : prev.line;
        var idx = cur ? cur.index : prev.index;
        push({
          event: 'remove_from_cart',
          _clear: true,
          ecommerce: {
            currency: cart.currency || currency,
            value: ((line.final_price != null ? line.final_price : line.price) * removedQty) / 100,
            items: [standardFromLine(line, idx, removedQty)]
          }
        });
      }
    });
    prevLines = next;
    lastCart = cart;
  }

  function fireAddToCart(parsedState) {
    if (!parsedState) return;
    var lines = parsedState.items && parsedState.items.length ? parsedState.items : [parsedState];
    lines.forEach(function (line, i) {
      var qty = line.quantity || 1;
      var value = (line.final_line_price != null
        ? line.final_line_price
        : (line.final_price != null ? line.final_price : line.price) * qty) / 100;
      push({
        event: 'add_to_cart',
        _clear: true,
        ecommerce: { currency: currency, value: value, items: [standardFromLine(line, i, qty)] }
      });
      push({
        event: 'add_to_cart_eyk',
        _clear: true,
        ecommerce: { currency: currency, value: String(value), items: [eykFromLine(line, i, qty)] }
      });
    });
  }

  function fireViewMiniCart(cart) {
    if (!cart) {
      push({ event: 'view_mini_cart', _clear: true, ecommerce: { currency: '', value: 0, items: [] } });
      return;
    }
    var items = cart.items || [];
    push({
      event: 'view_mini_cart',
      _clear: true,
      ecommerce: {
        currency: cart.currency || currency,
        value: (cart.items_subtotal_price || 0) / 100,
        items: items.map(function (l, i) { return standardFromLine(l, i); })
      }
    });
    if (items.length) {
      push({
        event: 'view_cart_eyk',
        _clear: true,
        ecommerce: {
          value: String((cart.items_subtotal_price || 0) / 100),
          currency: cart.currency || currency,
          cart_quantity: String(cart.item_count || 0),
          items: items.map(function (l, i) { return eykFromLine(l, i); })
        }
      });
    }
  }

  // ------------------------------------------------------------- interactions
  function onSelectItem(e) {
    var anchor = e.target.closest && e.target.closest('a[href]');
    if (!anchor || anchor.getAttribute('href').indexOf('/products/') === -1) return;
    var card = anchor.closest('[data-gtm-item]');
    if (!card) return;
    var d = parseCard(card);
    if (!d) return;
    push({
      event: 'select_item',
      _clear: true,
      ecommerce: {
        items: [standardFromData(d, { index: d.index, listId: d.list_id, listName: d.list_name, quantity: 1 })]
      }
    });
  }

  function onSelectFilter(e) {
    var input = e.target;
    if (!input || !input.closest) return;
    var group = input.closest('[data-gtm-filter-name]');
    if (!group) return;
    var name = group.getAttribute('data-gtm-filter-name');
    var value;
    var isPrice = /\.(gte|lte)$/.test(input.name || '') || input.type === 'range';
    if (isPrice) {
      var gte = group.querySelector('[name$=".gte"]');
      var lte = group.querySelector('[name$=".lte"]');
      value = (gte && gte.value ? gte.value : '0') + ' - ' + (lte && lte.value ? lte.value : '');
    } else {
      if (!input.checked) return;
      var label = input.closest('label') || group.querySelector('label[for="' + input.id + '"]');
      value = (label && label.textContent.trim()) || input.value;
    }
    push({ event: 'select_filter', filter_name: name, filter_value: value });
  }

  function onSelectPromotion(e) {
    var el = e.target.closest && e.target.closest('[data-gtm-promo-slot]');
    if (!el) return;
    var creative = el.getAttribute('data-gtm-promo-creative') || '';
    push({
      event: 'select_promotion',
      creative_name: creative ? creative.split('/').pop() : undefined,
      creative_slot: el.getAttribute('data-gtm-promo-slot'),
      promotion_name: el.getAttribute('data-gtm-promo-name') || undefined
    });
  }

  function onNewsletterSubmit(e) {
    if (!e.target || !e.target.closest) return;
    if (!e.target.closest('[data-gtm-newsletter]')) return;
    push({ event: 'submit_newsletter', path: window.location.pathname });
  }

  // ------------------------------------------------------------- cart drawer
  function observeCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof MutationObserver === 'undefined') return;
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName === 'open' && drawer.hasAttribute('open')) {
          fireViewMiniCart(lastCart);
        }
      });
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  // --------------------------------------------------------------------- init
  function init() {
    buildCategoryCache();
    firePageView();
    fireViewItem();
    fireViewItemList();
    fireView404();

    document.addEventListener('product-ajax:added', function (e) {
      fireAddToCart(e.detail && e.detail.product);
    });

    if (window.FoxTheme && FoxTheme.pubsub) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (data) {
        detectRemovals(data && data.cart);
      });
    }
    seedCart();
    observeCartDrawer();

    document.addEventListener('click', onSelectItem, true);
    document.addEventListener('click', onSelectPromotion, true);
    document.addEventListener('change', onSelectFilter, true);
    document.addEventListener('submit', onNewsletterSubmit, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
